import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useParams,
  useRouteMatch,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DETAILS_PAGE_TABS_MAPPING, SLICE_STATUS } from 'utils/constants';
import { TASK_BUNDLE_OPERATION_MODES } from 'utils/constants/taskBundles';
import { Status } from 'utils/customTypes';
import { useAppThunkDispatch } from 'state/store';
import {
  fetchCurrentBundle,
  fetchTaskTemplatesForBundle,
  selectActiveBundle,
  selectActiveBundleStatus,
  selectActiveBundleTemplatesStatus,
  selectActiveBundleTemplates,
  selectDependencyEdges,
  resetCurrentBundle,
} from 'state/ActiveTaskBundle/activeTaskBundleSlice';
import {
  fetchTaskTypes,
  selectCanFetchTaskTypes,
  selectTaskTypesFetchedWithV3,
  selectTaskTypesLoadingStatus,
} from 'state/TasksList/allTasks/allTasksSlice';
import {
  mapPathToOperationMode,
  createInitialTaskBundleDraft,
} from '../helpers';
import { TaskBundleMode, TaskBundleDraft } from '../types';

interface UseTaskBundlePageReturn {
  activeTab: number;
  onTabChange: (index: number) => void;
  mode: TaskBundleMode;
  status: Status;
  initialDraft: TaskBundleDraft;
}

export const useTaskBundlePage: () => UseTaskBundlePageReturn = () => {
  const dispatch = useAppThunkDispatch();
  const { newTaskTypes = false, taskDependenciesInBundles = false } =
    useFlags();
  const { taskBundleId } = useParams<{ taskBundleId: string }>();
  const bundleStatus = useSelector(selectActiveBundleStatus);
  const templatesStatus = useSelector(selectActiveBundleTemplatesStatus);
  const activeBundle = useSelector(selectActiveBundle);
  const activeBundleTemplates = useSelector(selectActiveBundleTemplates);
  const dependencyEdges = useSelector(selectDependencyEdges);

  const status: Status =
    bundleStatus === SLICE_STATUS.LOADING ||
    templatesStatus === SLICE_STATUS.LOADING
      ? SLICE_STATUS.LOADING
      : SLICE_STATUS.IDLE;

  const shouldFetchBundle = useMemo(() => {
    return Boolean(
      taskBundleId &&
        taskBundleId !== activeBundle.id &&
        status === SLICE_STATUS.IDLE
    );
  }, [taskBundleId, activeBundle.id, status]);
  const canFetchTaskTypes = useSelector(selectCanFetchTaskTypes);
  const taskTypesFetchedWithV3 = useSelector(selectTaskTypesFetchedWithV3);
  const taskTypesLoading = useSelector(selectTaskTypesLoadingStatus);

  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const activeTabParam = queryParams.get('activeTab');
  const mode: TaskBundleMode =
    mapPathToOperationMode.get(match.path) ??
    TASK_BUNDLE_OPERATION_MODES.CREATE;

  const [tabIndex, setTabIndex] = useState(
    activeTabParam ? DETAILS_PAGE_TABS_MAPPING[activeTabParam] : 0
  );

  const onTabChange = useCallback(
    (index: number) => {
      setTabIndex(index);
      history.replace(
        `${location.pathname}?activeTab=${
          Object.keys(DETAILS_PAGE_TABS_MAPPING)[index]
        }`
      );
    },
    [history, location.pathname]
  );

  const initialDraft = useMemo(() => {
    return createInitialTaskBundleDraft({
      activeBundle,
      activeBundleTemplates,
      mode,
      dependencyEdges,
    });
  }, [activeBundle, activeBundleTemplates, mode, dependencyEdges]);

  useEffect(() => {
    if (shouldFetchBundle) {
      dispatch(
        fetchCurrentBundle({ bundleId: taskBundleId, useNewApi: newTaskTypes })
      );
      dispatch(
        fetchTaskTemplatesForBundle({
          bundleId: taskBundleId,
          useNewApi: newTaskTypes,
          includeDependencies: newTaskTypes && taskDependenciesInBundles,
        })
      );
    }
  }, [
    shouldFetchBundle,
    newTaskTypes,
    dispatch,
    taskBundleId,
    taskDependenciesInBundles,
  ]);

  useEffect(() => {
    const versionMismatch =
      !taskTypesLoading && taskTypesFetchedWithV3 !== newTaskTypes;
    if (canFetchTaskTypes || versionMismatch) {
      dispatch(fetchTaskTypes(newTaskTypes));
    }
  }, [
    canFetchTaskTypes,
    dispatch,
    newTaskTypes,
    taskTypesFetchedWithV3,
    taskTypesLoading,
  ]);

  // Clear activeTaskBundle state when leaving the page
  useEffect(() => {
    return () => {
      dispatch(resetCurrentBundle());
    };
  }, [dispatch]);

  return {
    activeTab: tabIndex,
    onTabChange,
    mode,
    status,
    initialDraft,
  };
};
